import { useState, useEffect } from 'react';
import {
    Typography,
    Box,
    Card,
    CardContent,
    Grid,
    Divider,
    Chip,
    Avatar,
    useMediaQuery,
    Stack,
    Snackbar,
    Alert,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Breadcrumb } from 'components';
import { getAllReleaseNotesApi } from 'services';
import moment from 'moment';

export const ReleaseNotes = () => {
    const [getNotificationList, setGetNotificationList] = useState([]);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const isMediumScreen = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    const nav = [
        { name: 'Home', path: '/home' },
        { name: 'Release Notes', path: '/home/releaseNotes' },
    ];

    useEffect(() => {
        getAllReleaseNotes();
    }, []);

    const getAllReleaseNotes = () => {
        getAllReleaseNotesApi().then((res: any) => {
            if (res.status === 200) {
                if (res.data.releaseNotes.length > 0) {
                    setGetNotificationList(res.data.releaseNotes);
                } else {
                    setGetNotificationList([]);
                }
            } else {
                setError(true);
                setErrorMessage(res.data);
            }
        });
    };

    const resetListCounters = (html: string): string => {
        const container = document.createElement("div");
        container.innerHTML = html;

        const allLists = container.querySelectorAll("ol");
        allLists.forEach((list) => {
            let level = 0;
            list.querySelectorAll("li").forEach((li) => {
                const indent = li.className.includes("ql-indent-1")
                    ? "a"
                    : li.className.includes("ql-indent-2")
                        ? "i"
                        : "";

                if (!indent) {
                    level = 0;
                } else if (indent === "a") {
                    level = 1;
                }

                li.setAttribute("data-indent-level", `${level}`);
            });
        });

        return container.innerHTML;
    };

    const minHeight = isSmallScreen
        ? 'calc(100vh - 50px)'
        : isMediumScreen
            ? 'calc(100vh - 80px)'
            : 'calc(100vh - 100px)';

    return (
        <Box sx={{ padding: '16px', minHeight }}>
            {/* Breadcrumb Section */}
            <Box sx={{ marginBottom: '24px' }}>
                <Breadcrumb navList={nav} />
            </Box>

            {/* Page Title */}
            <Typography
                variant="h6"
                align="center"
                sx={{ fontWeight: 'bold', marginBottom: '16px', color: '#003883' }}
            >
                Release Notes
            </Typography>
            <Typography
                variant="subtitle1"
                align="center"
                sx={{ marginBottom: '32px', color: '#555' }}
            >
                Stay updated with the latest features, improvements, and bug fixes.
            </Typography>

            {/* Release Notes Grid */}
            <Grid container spacing={4}>
                {getNotificationList
                    .filter((note) => note.isPublished)
                    .map((note, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Card
                                variant="outlined"
                                sx={{
                                    boxShadow: 3,
                                    borderRadius: '8px',
                                    transition: 'transform 0.3s',
                                    '&:hover': {
                                        transform: 'scale(1.02)',
                                    },
                                }}
                            >
                                <CardContent>
                                    {/* Version and Date */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            marginBottom: '16px',
                                        }}
                                    >
                                        <Avatar
                                            sx={{
                                                bgcolor: '#003883',
                                                marginRight: '12px',
                                                width: 40,
                                                height: 40,
                                            }}
                                        >
                                            {index + 1}
                                        </Avatar>
                                        <Box>
                                            <Typography
                                                variant="h6"
                                                sx={{ fontWeight: 'bold', color: '#003883' }}
                                            >
                                                Version {note.versionNumber}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                Released on{' '}
                                                {moment(note.releaseDate).format('DD/MM/YYYY')}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Divider sx={{ marginY: 2 }} />

                                    {/* What's New Section */}
                                    <Typography
                                        variant="body1"
                                        sx={{ marginBottom: '8px', fontWeight: 'bold' }}
                                    >
                                        What's New:
                                    </Typography>
                                    
                                    {/* Quill Editor Output */}
                                    <div className='quill-output'>
                                        <div
                                            className="ql-editor"
                                            dangerouslySetInnerHTML={{ __html: resetListCounters(note.notes) }}
                                        />
                                    </div>

                                    {/* Labels as Chips */}
                                    <Box sx={{ marginTop: '16px' }}>
                                        {note.labels &&
                                            note.labels.split(',').map((label: string, index: number) => {
                                                const trimmedLabel = label.trim();
                                                let chipColor: 'primary' | 'success' | 'error' = 'primary';

                                                if (
                                                    trimmedLabel.toLowerCase() === 'feature update' ||
                                                    trimmedLabel.toLowerCase() === 'security patch'
                                                ) {
                                                    chipColor = 'primary';
                                                } else if (trimmedLabel.toLowerCase() === 'performance improvement') {
                                                    chipColor = 'success';
                                                } else if (trimmedLabel.toLowerCase() === 'bug fix') {
                                                    chipColor = 'error';
                                                }

                                                return (
                                                    <Chip
                                                        key={index}
                                                        label={trimmedLabel}
                                                        color={chipColor}
                                                        size="small"
                                                        sx={{
                                                            marginRight: '8px',
                                                            marginBottom: '8px',
                                                        }}
                                                    />
                                                );
                                            })}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
            </Grid>

            {/* Error Snackbar */}
            <Stack sx={{ width: '100%', marginTop: 2 }} spacing={2}>
                <Snackbar
                    open={error}
                    autoHideDuration={6000}
                    onClose={() => setError(false)}
                >
                    <Alert
                        severity="error"
                        onClose={() => setError(false)}
                    >
                        <b>{errorMessage}</b>
                    </Alert>
                </Snackbar>
            </Stack>
        </Box >
    );
};